import React, { useEffect, useMemo, useRef, useState } from 'react'
import AutoGrid from '../AutoGrid'
import { createPortal } from 'react-dom'
import { Accessibility, Activity, ArrowRight, BarChart3, BookOpen, Building2, Handshake, Layers, List, Shield, Smile, Stethoscope, Target, TrendingUp, Users, X } from 'lucide-react'
import PdzDialogList from './PdzDialogList'
import BSDCard from '../BSDCard'
import DistrictMap from '../DistrictMap'
import { AMBITI_STATS, type AmbitoId } from '../../data/ambitiStats'
import { Link } from 'react-router-dom'
import { pianiDiZona, CLUSTER_TARGET_VALUES, type ClusterTarget, type ObiettivoCluster, type SchedaObiettivo } from '../../data/pianiDiZona'
import { pdzSchedaSlug } from './PdzDetailPage'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, LabelList, PieChart, Pie } from 'recharts'
import { CHART_TOOLTIP_STYLE, AMBITO_COMPARE_COLORS, formatInt } from '../../utils/dashboardFormatters'
import CompareAmbitiBarCard from '../shared/CompareAmbitiBarCard'

const AMBITO_TO_PDZ_KEY: Record<AmbitoId, string> = {
  desio: 'Desio',
  vimercate: 'Vimercate',
  monza: 'Monza',
  seregno: 'Seregno',
  carate: 'Carate',
}

const CLUSTER_COLORS: Record<ClusterTarget, string> = {
  Adulti: '#3b82f6',
  Minori: '#10b981',
  Anziani: '#f59e0b',
}

const PDZ_OBIETTIVO_CLUSTER_COLORS: Record<ObiettivoCluster, string> = {
  'Inclusione': '#0ea5e9',
  'Benessere sociale': '#7c3aed',
  'Sistema': '#10b981',
  Interambito: '#f59e0b',
}

const AZIONI_AXIS_LABEL_MAX_CHARS = 12
const AZIONI_AXIS_LABEL_WIDTH = 80
const BENEFICIARI_AXIS_LABEL_WIDTH = 76

type PdzSchedaListItem = {
  itemKey: string
  gruppoId: string
  gruppoTitolo: string
  gruppoColore: string
  gruppoIcona: string
  scheda: SchedaObiettivo
}

function wrapAxisLabel(value: string, maxChars = AZIONI_AXIS_LABEL_MAX_CHARS): string[] {
  const clean = value.trim()
  if (!clean) return ['']

  const words = clean.split(/\s+/)
  const lines: string[] = []
  let currentLine = ''

  for (const word of words) {
    if (!currentLine) {
      currentLine = word
      continue
    }

    if (`${currentLine} ${word}`.length <= maxChars) {
      currentLine = `${currentLine} ${word}`
    } else {
      lines.push(currentLine)
      currentLine = word
    }
  }

  if (currentLine) lines.push(currentLine)
  return lines
}

function AzioniPieTooltip({
  active,
  payload,
}: {
  active?: boolean
  payload?: Array<{ name?: string; value?: number; payload?: { name?: string; value?: number } }>
}) {
  if (!active || !payload || payload.length === 0) return null

  const point = payload[0]
  const nome = point?.payload?.name ?? point?.name ?? 'Gruppo'
  const valore = point?.payload?.value ?? point?.value ?? 0

  return (
    <div
      style={{
        background: 'var(--tooltip-bg)',
        border: '1px solid var(--tooltip-border)',
        borderRadius: 8,
        fontSize: 12,
        color: 'var(--tooltip-text)',
        padding: '8px 10px',
      }}
    >
      <div style={{ color: 'var(--text-0)', fontWeight: 600, marginBottom: 2 }}>{nome}</div>
      <div style={{ color: 'var(--text-2)' }}>{`Azioni: ${formatInt(valore)}`}</div>
    </div>
  )
}

function SchedeBarTooltip({
  active,
  payload,
}: {
  active?: boolean
  payload?: Array<{ value?: number; payload?: { name?: string; count?: number } }>
}) {
  if (!active || !payload || payload.length === 0) return null

  const point = payload[0]
  const nome = point?.payload?.name ?? 'Gruppo'
  const valore = point?.payload?.count ?? point?.value ?? 0

  return (
    <div
      style={{
        background: 'var(--tooltip-bg)',
        border: '1px solid var(--tooltip-border)',
        borderRadius: 8,
        fontSize: 12,
        color: 'var(--tooltip-text)',
        padding: '8px 10px',
      }}
    >
      <div style={{ color: 'var(--text-0)', fontWeight: 600, marginBottom: 2 }}>{nome}</div>
      <div style={{ color: 'var(--text-2)' }}>{`Obiettivi: ${formatInt(Number(valore))}`}</div>
    </div>
  )
}

function LeftAlignedAxisTick({
  y = 0,
  payload,
}: {
  y?: number
  payload?: { value?: string | number }
}) {
  const lines = wrapAxisLabel(String(payload?.value ?? ''))

  return (
    <text x={0} y={y} fill="var(--text-2)" fontSize={10} textAnchor="start">
      {lines.map((line, index) => (
        <tspan key={`${line}-${index}`} x={0} dy={index === 0 ? 4 : 11}>
          {line}
        </tspan>
      ))}
    </text>
  )
}

function SchedaDetailCard({ scheda, coloreGruppo, onSelect }: { scheda: SchedaObiettivo; coloreGruppo: string; onSelect?: () => void }) {
  return (
    <button
      className="pdz-scheda-card"
      type="button"
      onClick={() => onSelect?.()}
      style={{
        width: '100%',
        background: 'transparent',
        border: 'none',
        borderLeft: `2px solid ${coloreGruppo}`,
        borderRadius: 0,
        overflow: 'hidden',
        cursor: 'pointer',
        padding: '0 0 0 6px',
        textAlign: 'left',
        lineHeight: 1.4,
      }}
    >
      <span style={{ fontWeight: 600, color: 'var(--text-1)', fontSize: 11, display: 'block' }}>{scheda.titolo}</span>
      {scheda.sottotitolo && <span style={{ color: 'var(--text-3)', fontSize: 11, display: 'block' }}>{scheda.sottotitolo}</span>}
    </button>
  )
}

function SchedaDetailFull({ scheda, coloreGruppo, gruppoTitolo, ambitoLabel }: { scheda: SchedaObiettivo; coloreGruppo: string; gruppoTitolo: string; ambitoLabel: string }) {
  const obiettivoClusterColor = PDZ_OBIETTIVO_CLUSTER_COLORS[scheda.obiettivo_cluster] ?? coloreGruppo
  const hasIndicatori = (scheda.indicatoriOutput?.length ?? 0) > 0 || (scheda.indicatoriOutcome?.length ?? 0) > 0
  const hasPartners = !!(scheda.partners && scheda.partners.length > 0)

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <div className="cdc-detail-header" style={{ marginBottom: 0, paddingBottom: 14 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            fontSize: 12, fontWeight: 700, padding: '4px 12px', borderRadius: 999,
            color: coloreGruppo, background: `${coloreGruppo}18`, border: `1px solid ${coloreGruppo}44`,
          }}>
            {gruppoTitolo}
          </span>
          <span className="cdc-detail-ambito-tag">
            <Layers size={12} /> {ambitoLabel}
          </span>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            fontSize: 12, fontWeight: 600, padding: '4px 12px', borderRadius: 999,
            color: obiettivoClusterColor, background: `${obiettivoClusterColor}15`, border: `1px solid ${obiettivoClusterColor}40`,
          }}>
            {scheda.obiettivo_cluster}
          </span>
        </div>

        <h1 className="cdc-detail-title" style={{ fontSize: 28, marginBottom: 8 }}>{scheda.titolo}</h1>
        {scheda.sottotitolo && <p style={{ color: 'var(--text-2)', fontSize: 13, fontStyle: 'italic', margin: 0 }}>— {scheda.sottotitolo}</p>}

        <div className="cdc-detail-meta" style={{ marginTop: 10 }}>
          {scheda.target_cluster.map(c => (
            <span key={c} style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 999,
              color: CLUSTER_COLORS[c], background: `${CLUSTER_COLORS[c]}15`, border: `1px solid ${CLUSTER_COLORS[c]}40`,
            }}>
              <Users size={11} /> {c}
            </span>
          ))}
        </div>
      </div>

      <div className="cdc-detail-row" style={{ marginBottom: 0, gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}>
        <div className="cdc-detail-info-card" style={{ padding: 18 }}>
          <h2 className="cdc-detail-section-title"><Target size={16} /> Obiettivo e target</h2>
          <dl className="cdc-detail-dl">
            <div>
              <dt>Obiettivo</dt>
              <dd style={{ alignItems: 'flex-start' }}>{scheda.obiettivo}</dd>
            </div>
            <div>
              <dt>Target</dt>
              <dd style={{ alignItems: 'flex-start' }}>{scheda.target}</dd>
            </div>
            {scheda.cooperazioneAltriAmbiti && (
              <div>
                <dt>Cooperazione</dt>
                <dd style={{ alignItems: 'flex-start' }}>{scheda.cooperazioneAltriAmbiti}</dd>
              </div>
            )}
          </dl>
        </div>

        <div className="cdc-detail-info-card" style={{ padding: 18 }}>
          <h2 className="cdc-detail-section-title"><BookOpen size={16} /> Scheda</h2>
          <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
            <div style={{ textAlign: 'center', flex: 1, background: 'var(--bg-2)', borderRadius: 10, padding: '12px 8px', border: '1px solid var(--border)' }}>
              <div style={{ fontSize: 24, fontWeight: 800, fontFamily: 'var(--font-mono)', color: coloreGruppo, lineHeight: 1 }}>{scheda.azioni.length}</div>
              <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 4, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Azioni</div>
            </div>
            <div style={{ textAlign: 'center', flex: 1, background: 'var(--bg-2)', borderRadius: 10, padding: '12px 8px', border: '1px solid var(--border)' }}>
              <div style={{ fontSize: 24, fontWeight: 800, fontFamily: 'var(--font-mono)', color: coloreGruppo, lineHeight: 1 }}>{scheda.risorse.length}</div>
              <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 4, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Risorse</div>
            </div>
            {hasPartners && (
              <div style={{ textAlign: 'center', flex: 1, background: 'var(--bg-2)', borderRadius: 10, padding: '12px 8px', border: '1px solid var(--border)' }}>
                <div style={{ fontSize: 24, fontWeight: 800, fontFamily: 'var(--font-mono)', color: coloreGruppo, lineHeight: 1 }}>{scheda.partners!.length}</div>
                <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 4, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Partner</div>
              </div>
            )}
          </div>
          {scheda.areePolicy && scheda.areePolicy.length > 0 && (
            <>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>Aree di Policy</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                {scheda.areePolicy.map((area, idx) => (
                  <span key={idx} style={{ fontSize: 12, color: 'var(--text-2)', background: 'var(--bg-3)', padding: '3px 10px', borderRadius: 6, border: '1px solid var(--border)' }}>
                    {area}
                  </span>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      <div className="cdc-detail-services-card" style={{ marginBottom: 0, padding: 18 }}>
        <h2 className="cdc-detail-section-title"><Building2 size={16} /> Azioni Programmate <span className="cdc-detail-services-count">{scheda.azioni.length}</span></h2>
        <div className="cdc-detail-sezione" style={{ borderLeft: `3px solid ${coloreGruppo}` }}>
          <div className="cdc-detail-servizi-grid">
            {scheda.azioni.map((azione, idx) => (
              <div key={idx} className="cdc-detail-servizio">
                <p className="cdc-detail-servizio-desc" style={{ margin: 0, fontSize: 13 }}>
                  <span style={{ fontWeight: 700, color: 'var(--text-0)', marginRight: 6, fontFamily: 'var(--font-mono)', fontSize: 11 }}>
                    {String(idx + 1).padStart(2, '0')}
                  </span>
                  {azione}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="cdc-detail-row" style={{ marginBottom: 0, gridTemplateColumns: hasPartners ? 'repeat(auto-fit, minmax(260px, 1fr))' : '1fr' }}>
        <div className="cdc-detail-info-card" style={{ padding: 18 }}>
          <h2 className="cdc-detail-section-title"><TrendingUp size={16} /> Risorse</h2>
          <div className="cdc-detail-sezione">
            <div className="cdc-detail-servizi-grid">
              {scheda.risorse.map((risorsa, idx) => (
                <div key={idx} className="cdc-detail-servizio">
                  <p className="cdc-detail-servizio-desc" style={{ margin: 0, fontSize: 13 }}>{risorsa}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {hasPartners && (
          <div className="cdc-detail-info-card" style={{ padding: 18 }}>
            <h2 className="cdc-detail-section-title"><Handshake size={16} /> Partner</h2>
            <div className="cdc-detail-sezione">
              <div className="cdc-detail-servizi-grid">
                {scheda.partners!.map((partner, idx) => (
                  <div key={idx} className="cdc-detail-servizio">
                    <p className="cdc-detail-servizio-desc" style={{ margin: 0, fontSize: 13 }}>{partner}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {hasIndicatori && (
        <div className="cdc-detail-services-card" style={{ marginBottom: 0, padding: 18 }}>
          <h2 className="cdc-detail-section-title"><TrendingUp size={16} /> Indicatori</h2>
          <div className="cdc-detail-sections" style={{ gap: 14 }}>
            {scheda.indicatoriOutput && scheda.indicatoriOutput.length > 0 && (
              <div className="cdc-detail-sezione">
                <h3 className="cdc-detail-sezione-title">Output</h3>
                <div className="cdc-detail-servizi-grid">
                  {scheda.indicatoriOutput.map((indicatore, idx) => (
                    <div key={idx} className="cdc-detail-servizio">
                      <p className="cdc-detail-servizio-desc" style={{ margin: 0, fontSize: 13 }}>
                        <span style={{ color: coloreGruppo, marginRight: 6 }}>→</span>{indicatore}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {scheda.indicatoriOutcome && scheda.indicatoriOutcome.length > 0 && (
              <div className="cdc-detail-sezione">
                <h3 className="cdc-detail-sezione-title">Outcome</h3>
                <div className="cdc-detail-servizi-grid">
                  {scheda.indicatoriOutcome.map((indicatore, idx) => (
                    <div key={idx} className="cdc-detail-servizio">
                      <p className="cdc-detail-servizio-desc" style={{ margin: 0, fontSize: 13 }}>
                        <span style={{ color: coloreGruppo, marginRight: 6 }}>→</span>{indicatore}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}


function PdzElencoObiettiviContent({
  items,
  selectedAmbitoId,
  emptyLabel,
}: {
  items: PdzSchedaListItem[]
  selectedAmbitoId: AmbitoId | null
  emptyLabel?: string
}) {
  const [page, setPage] = useState(0)
  const [selectedItem, setSelectedItem] = useState<PdzSchedaListItem | null>(null)
  const listRef = useRef<HTMLDivElement>(null)

  function goToPage(i: number) {
    setPage(i)
    listRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
  }

  useEffect(() => {
    setPage(0)
    setSelectedItem(null)
  }, [items])

  if (items.length === 0) {
    return (
      <div style={{ color: 'var(--text-2)', fontSize: 12 }}>
        {emptyLabel ?? 'Nessuna scheda disponibile'}
      </div>
    )
  }

  const PDZ_PER_PAGE = 5
  const totalPages = Math.ceil(items.length / PDZ_PER_PAGE)
  const paginated = items.slice(page * PDZ_PER_PAGE, (page + 1) * PDZ_PER_PAGE)

  return (
    <>
        <div style={{ display: 'grid', gap: 4 }}>
          <div className="pdz-elenco-list" ref={listRef} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {paginated.map(item => (
            <div key={item.itemKey}>
              <SchedaDetailCard
                scheda={item.scheda}
                coloreGruppo={item.gruppoColore}
                onSelect={() => setSelectedItem(item)}
              />
            </div>
          ))}
        </div>
        {totalPages > 1 && (
          <div className="pdz-elenco-pagination" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 10 }}>
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                className="pdz-elenco-page-dot"
                key={i}
                type="button"
                onClick={() => goToPage(i)}
                aria-label={`Vai alla pagina ${i + 1}`}
                title={`Pagina ${i + 1}`}
                style={{
                  width: 10,
                  height: 10,
                  padding: 0,
                  borderRadius: 999,
                  background: i === page ? 'var(--accent)' : 'var(--border-md)',
                  border: i === page ? '1px solid var(--accent)' : '1px solid transparent',
                  cursor: 'pointer',
                  opacity: i === page ? 1 : 0.9,
                  flexShrink: 0,
                }}
              />
            ))}
          </div>
        )}
      </div>

      {selectedItem && typeof document !== 'undefined' ? createPortal(
        <div
          role="dialog"
          aria-modal="true"
          aria-label={`Dettaglio obiettivo ${selectedItem.scheda.titolo}`}
          onClick={() => setSelectedItem(null)}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 10020,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
          }}
        >
          <div
            onClick={(event) => event.stopPropagation()}
            style={{
              width: 'min(920px, 94vw)',
              maxHeight: '86vh',
              overflowY: 'auto',
              background: 'var(--tooltip-bg)',
              border: '1px solid var(--tooltip-border)',
              borderRadius: 12,
              color: 'var(--tooltip-text)',
              boxShadow: '0 14px 42px rgba(0,0,0,0.35)',
              padding: 16,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 12 }}>
              <div style={{ color: 'var(--text-0)', fontSize: 14, fontWeight: 700 }}>Dettaglio obiettivo</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <Link
                  to={`/pianodizona/${(selectedAmbitoId ? AMBITO_TO_PDZ_KEY[selectedAmbitoId] : 'vimercate').toLowerCase()}/${pdzSchedaSlug(selectedItem.scheda.titolo)}`}
                  state={{ from: '/pianidizona' }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 5,
                    color: 'var(--text-2)', fontSize: 12, fontWeight: 600, textDecoration: 'none',
                  }}
                >
                  Apri pagina
                  <ArrowRight style={{ width: 14, height: 14 }} />
                </Link>
                <button
                  type="button"
                  onClick={() => setSelectedItem(null)}
                  aria-label="Chiudi dettaglio obiettivo"
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 8,
                    border: '1px solid var(--border-md)',
                    background: 'var(--bg-2)',
                    color: 'var(--text-2)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                  }}
                >
                  <X size={15} />
                </button>
              </div>
            </div>
            <SchedaDetailFull
              scheda={selectedItem.scheda}
              coloreGruppo={selectedItem.gruppoColore}
              gruppoTitolo={selectedItem.gruppoTitolo}
              ambitoLabel={selectedAmbitoId ? `Ambito di ${AMBITI_STATS.find(a => a.id === selectedAmbitoId)?.nome ?? ''}` : 'Piani di Zona MB'}
            />
          </div>
        </div>,
        document.body,
      ) : null}
    </>
  )
}

function GruppoSchedePanel({
  id,
  titolo,
  colore,
  icona,
  schede,
}: {
  id: string
  titolo: string
  colore: string
  icona: string
  schede: SchedaObiettivo[]
}) {
  const [open, setOpen] = useState(false)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      <button
        onClick={() => setOpen(prev => !prev)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '10px 0',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          width: '100%',
          borderBottom: `1px solid ${colore}30`,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 16 }}>{icona}</span>
          <span style={{ color: 'var(--text-2)', fontWeight: 700, fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            {titolo}
          </span>
          <span style={{ fontSize: 11, color: 'var(--text-3)', background: 'var(--bg-3)', padding: '1px 7px', borderRadius: 99 }}>
            {schede.length} schede
          </span>
        </div>
        <span style={{ color: 'var(--text-3)', fontSize: 15 }}>{open ? '▴' : '▾'}</span>
      </button>

      {open && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingTop: 10 }}>
          {schede.map(scheda => (
            <SchedaDetailCard key={`${id}-${scheda.id}`} scheda={scheda} coloreGruppo={colore} />
          ))}
        </div>
      )}
    </div>
  )
}

export default function PianiDiZonaSection({
  selectedAmbitoId,
  onSelectedAmbitoChange,
}: {
  selectedAmbitoId: AmbitoId | null
  onSelectedAmbitoChange: (ambitoId: AmbitoId | null) => void
}) {
  const pdzData = useMemo(() => {
    const selectedPiano = selectedAmbitoId ? pianiDiZona[AMBITO_TO_PDZ_KEY[selectedAmbitoId]] : null
    const pianiAttivi = selectedAmbitoId
      ? (() => {
          const key = AMBITO_TO_PDZ_KEY[selectedAmbitoId]
          const piano = pianiDiZona[key]
          return piano ? [piano] : []
        })()
      : Object.values(pianiDiZona).filter((piano): piano is NonNullable<typeof piano> => piano !== null)

    const gruppiMap = new Map<string, { id: string; name: string; schedeCount: number; azioniCount: number; color: string }>()
    const clusterColorMap: Record<ClusterTarget, string> = {
      Adulti: '#3b82f6',
      Minori: '#10b981',
      Anziani: '#f59e0b',
    }
    const clusterMap = new Map<ClusterTarget, { id: string; name: ClusterTarget; count: number; color: string }>(
      CLUSTER_TARGET_VALUES.map(cluster => [
        cluster,
        {
          id: cluster.toLowerCase(),
          name: cluster,
          count: 0,
          color: clusterColorMap[cluster],
        },
      ])
    )
    let totaleSchede = 0
    let totaleAzioni = 0

    for (const piano of pianiAttivi) {
      for (const gruppo of piano.gruppi) {
        const schedeCount = gruppo.schede.length
        const azioniCount = gruppo.schede.reduce((acc, scheda) => acc + scheda.azioni.length, 0)
        totaleSchede += schedeCount
        totaleAzioni += azioniCount

        const prev = gruppiMap.get(gruppo.id)
        if (prev) {
          prev.schedeCount += schedeCount
          prev.azioniCount += azioniCount
        } else {
          gruppiMap.set(gruppo.id, {
            id: gruppo.id,
            name: gruppo.titolo,
            schedeCount,
            azioniCount,
            color: gruppo.colore,
          })
        }

        for (const scheda of gruppo.schede) {
          const clusterScheda = new Set(scheda.target_cluster ?? [])
          for (const key of clusterScheda) {
            const cluster = clusterMap.get(key)
            if (cluster) cluster.count += 1
          }
        }
      }
    }

    const schedePerCluster = Array.from(clusterMap.values())
      .sort((a, b) => b.count - a.count)

    const azioniPerGruppo = Array.from(gruppiMap.values())
      .map(g => ({ id: g.id, name: g.name, value: g.azioniCount, color: g.color }))
      .sort((a, b) => b.value - a.value)

    const gruppoDettagliMap = new Map<string, { id: string; titolo: string; colore: string; icona: string; schede: SchedaObiettivo[] }>()
    const obiettivoClusterMap = new Map<ObiettivoCluster, { id: ObiettivoCluster; name: ObiettivoCluster; count: number; color: string }>(
      (Object.keys(PDZ_OBIETTIVO_CLUSTER_COLORS) as ObiettivoCluster[]).map(cluster => [
        cluster,
        {
          id: cluster,
          name: cluster,
          count: 0,
          color: PDZ_OBIETTIVO_CLUSTER_COLORS[cluster],
        },
      ])
    )

    for (const piano of pianiAttivi) {
      for (const gruppo of piano.gruppi) {
        const prev = gruppoDettagliMap.get(gruppo.id)
        if (prev) {
          prev.schede.push(...gruppo.schede)
        } else {
          gruppoDettagliMap.set(gruppo.id, {
            id: gruppo.id,
            titolo: gruppo.titolo,
            colore: gruppo.colore,
            icona: gruppo.icona,
            schede: [...gruppo.schede],
          })
        }

        for (const scheda of gruppo.schede) {
          const cluster = obiettivoClusterMap.get(scheda.obiettivo_cluster)
          if (cluster) cluster.count += 1
        }
      }
    }

    const gruppoDettagli = Array.from(gruppoDettagliMap.values())
    const obiettivoClusterDettagli = Array.from(obiettivoClusterMap.values())

    const allSchede = pianiAttivi.flatMap(p => p.gruppi.flatMap(g => g.schede))
    const focusDisabilitaCount = allSchede.filter(s => s.focus_disabilita).length
    const coinvolgimentoAsstCount = allSchede.filter(s => s.coinvolgimentoASST).length
    const mediaAzioniPerObiettivo = totaleSchede > 0 ? totaleAzioni / totaleSchede : 0
    const statoServizioRaw = { nuovo: 0, continuita: 0, potenziamento: 0, rivisto: 0 }
    for (const s of allSchede) {
      if (s.statoServizio) statoServizioRaw[s.statoServizio]++
    }
    const statoServizioData = [
      { name: 'Continuità', value: statoServizioRaw.continuita, color: '#10b981' },
      { name: 'Nuovo', value: statoServizioRaw.nuovo, color: '#7c3aed' },
      { name: 'Potenziamento', value: statoServizioRaw.potenziamento, color: '#0ea5e9' },
      { name: 'Rivisto', value: statoServizioRaw.rivisto, color: '#f59e0b' },
    ].filter(d => d.value > 0)

    return {
      totaleSchede,
      totaleAzioni,
      schedePerCluster,
      azioniPerGruppo,
      gruppoDettagli,
      obiettivoClusterDettagli,
      hasSelectedPiano: gruppoDettagli.length > 0,
      titolo: selectedAmbitoId ? 'Piano di Zona 2025 - 2027' : 'Piani di Zona 2025 - 2027',
      focusDisabilitaCount,
      coinvolgimentoAsstCount,
      mediaAzioniPerObiettivo,
      statoServizioData,
      totaleAllSchede: allSchede.length,
    }
  }, [selectedAmbitoId])

  const azioniPerGruppoChartHeight = useMemo(() => {
    const totalHeight = pdzData.azioniPerGruppo.reduce((acc, gruppo) => {
      const linesCount = wrapAxisLabel(gruppo.name).length
      const rowHeight = Math.max(34, linesCount * 12 + 14)
      return acc + rowHeight
    }, 0)

    return Math.max(220, totalHeight)
  }, [pdzData.azioniPerGruppo])

  const pdzSchedeDettaglioItems = useMemo(() => {
    return pdzData.gruppoDettagli.flatMap(gruppo =>
      gruppo.schede.map((scheda, index) => ({
        itemKey: `${gruppo.id}-${scheda.id}-${index}`,
        gruppoId: gruppo.id,
        gruppoTitolo: gruppo.titolo,
        gruppoColore: PDZ_OBIETTIVO_CLUSTER_COLORS[scheda.obiettivo_cluster],
        gruppoIcona: gruppo.icona,
        scheda,
      }))
    )
  }, [pdzData.gruppoDettagli])

  const pdzCompareData = useMemo(() => {
    return AMBITI_STATS.map(ambito => {
      const piano = pianiDiZona[AMBITO_TO_PDZ_KEY[ambito.id]]
      const color = AMBITO_COMPARE_COLORS[ambito.id]
      if (!piano) {
        return { id: ambito.id, name: ambito.nome, color, totaleSchede: 0, totaleAzioni: 0, inclusione: 0, benessere: 0, sistema: 0, interambito: 0, focusDisabilita: 0, asst: 0, adulti: 0, minori: 0, anziani: 0 }
      }
      const allSchede = piano.gruppi.flatMap(g => g.schede)
      return {
        id: ambito.id,
        name: ambito.nome,
        color,
        totaleSchede: allSchede.length,
        totaleAzioni: allSchede.reduce((acc, s) => acc + s.azioni.length, 0),
        inclusione: allSchede.filter(s => s.obiettivo_cluster === 'Inclusione').length,
        benessere: allSchede.filter(s => s.obiettivo_cluster === 'Benessere sociale').length,
        sistema: allSchede.filter(s => s.obiettivo_cluster === 'Sistema').length,
        interambito: allSchede.filter(s => s.obiettivo_cluster === 'Interambito').length,
        focusDisabilita: allSchede.filter(s => s.focus_disabilita).length,
        asst: allSchede.filter(s => s.coinvolgimentoASST).length,
        adulti: allSchede.filter(s => s.target_cluster.includes('Adulti')).length,
        minori: allSchede.filter(s => s.target_cluster.includes('Minori')).length,
        anziani: allSchede.filter(s => s.target_cluster.includes('Anziani')).length,
      }
    })
  }, [])

  return (
    <>
      <h3 className="telemetry-section-title" style={{ marginBottom: 8 }}>
        {selectedAmbitoId
          ? `Ambito di ${AMBITI_STATS.find(a => a.id === selectedAmbitoId)?.nome ?? ''} - Piano di Zona`
          : 'Provincia di Monza e Brianza - Piano di Zona'}
      </h3>

      <AutoGrid>


        <BSDCard className="telemetry-map-card" kind="kpi" sezione="Dati demografici" style={{ minHeight: 120, padding: 10 }}>
          <DistrictMap
            onSelectDistrict={(id) => onSelectedAmbitoChange(id as AmbitoId)}
            onClearSelection={() => onSelectedAmbitoChange(null)}
            selectedDistrictId={selectedAmbitoId ?? null}
          />
        </BSDCard>

        <BSDCard
            kind="kpi"
            sezione="Piano di Zona"
            label="Obiettivi"
            value={String(pdzSchedeDettaglioItems.length)}
            icon={List}
            color="#6366f1"
            zoomable
            hideZoomButton
            sub={<p style={{ fontSize: 12, color: 'var(--text-2)', lineHeight: 1.6, margin: 0 }}>Il <strong>Piano di Zona (PDZ)</strong> 2025-2027 è lo strumento di programmazione territoriale con cui gli Ambiti definiscono <strong>obiettivi</strong>, priorità, azioni e risorse per organizzare il sistema locale dei servizi sociali e sociosanitari. Gli <strong>obiettivi</strong> rappresentano i risultati attesi che il Piano di Zona si propone di raggiungere.</p>}
            zoomSub={
              <PdzDialogList
                items={pdzSchedeDettaglioItems}
                selectedAmbitoId={selectedAmbitoId ?? null}
                emptyLabel="Nessuna scheda disponibile"
                detailContent={(item) => (
                  <SchedaDetailFull
                    scheda={item.scheda}
                    coloreGruppo={item.gruppoColore}
                    gruppoTitolo={item.gruppoTitolo}
                    ambitoLabel={selectedAmbitoId ? `Ambito di ${AMBITI_STATS.find(a => a.id === selectedAmbitoId)?.nome ?? ''}` : 'Piani di Zona MB'}
                  />
                )}
              />
            }
            footer={(openZoom) => (
              <button
                type="button"
                onClick={openZoom}
                style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%', padding: '6px 0', fontSize: 12, fontWeight: 600, color: '#6366f1', background: 'none', border: 'none', borderTop: '1px solid var(--border)', cursor: 'pointer', justifyContent: 'center', marginTop: 8, paddingTop: 8 }}
              >
                <List size={14} />
                Vedi elenco completo
              </button>
            )}
          />

        

        <BSDCard
          kind="panel"
          zoomable
          sezione="Piano di Zona"
          style={{ display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}
        >
          <h3 className="telemetry-section-title" style={{ marginTop: 0, marginBottom: 8 }}>Aree intervento</h3>
          <div style={{ flex: 1, minHeight: 0, overflowY: 'hidden', overflowX: 'hidden' }}>
            <ResponsiveContainer width="100%" height={126}>
              <BarChart
                data={[...pdzData.obiettivoClusterDettagli].sort((a, b) => b.count - a.count)}
                layout="vertical"
                margin={{ top: 2, right: 22, bottom: 2, left: 6 }}
              >
                <XAxis type="number" hide allowDecimals={false} />
                <YAxis
                  type="category"
                  dataKey="name"
                  tick={<LeftAlignedAxisTick />}
                  axisLine={false}
                  tickLine={false}
                  width={BENEFICIARI_AXIS_LABEL_WIDTH}
                />
                <Tooltip content={<SchedeBarTooltip />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
                <Bar dataKey="count" radius={[0, 6, 6, 0]} barSize={11}>
                  <LabelList
                    dataKey="count"
                    position="right"
                    formatter={value => formatInt(Number(value ?? 0))}
                    fill="var(--text-2)"
                    fontSize={10}
                  />
                  {[...pdzData.obiettivoClusterDettagli].sort((a, b) => b.count - a.count).map((entry, idx) => (
                    <Cell key={`${entry.id}-${idx}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </BSDCard>

      <BSDCard
            kind="kpi"
            sezione="Piano di Zona"
            label="Benessere sociale"
            value={String(pdzSchedeDettaglioItems.filter(item => item.scheda.obiettivo_cluster === 'Benessere sociale').length)}
            icon={Smile}
            color="#6366f1"
            zoomable
            hideZoomButton
            sub={<p style={{ fontSize: 12, color: 'var(--text-2)', lineHeight: 1.6, margin: 0 }}><strong>Obiettivi</strong> volti a migliorare la qualità della vita e il benessere complessivo della comunità locale.</p>}
            zoomSub={
              <PdzDialogList
                items={pdzSchedeDettaglioItems.filter(item => item.scheda.obiettivo_cluster === 'Benessere sociale')}
                selectedAmbitoId={selectedAmbitoId ?? null}
                emptyLabel="Nessuna scheda in Benessere sociale"
                detailContent={(item) => (
                  <SchedaDetailFull
                    scheda={item.scheda}
                    coloreGruppo={item.gruppoColore}
                    gruppoTitolo={item.gruppoTitolo}
                    ambitoLabel={selectedAmbitoId ? `Ambito di ${AMBITI_STATS.find(a => a.id === selectedAmbitoId)?.nome ?? ''}` : 'Piani di Zona MB'}
                  />
                )}
              />
            }
            footer={(openZoom) => (
              <button
                type="button"
                onClick={openZoom}
                style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%', padding: '6px 0', fontSize: 12, fontWeight: 600, color: '#6366f1', background: 'none', border: 'none', borderTop: '1px solid var(--border)', cursor: 'pointer', justifyContent: 'center', marginTop: 8, paddingTop: 8 }}
              >
                <List size={14} />
                Vedi elenco completo
              </button>
            )}
          />
    
        
        <BSDCard
            kind="kpi"
            sezione="Piano di Zona"
            label="Inclusione"
            value={String(pdzSchedeDettaglioItems.filter(item => item.scheda.obiettivo_cluster === 'Inclusione').length)}
            icon={Handshake}
            color="#6366f1"
            zoomable
            hideZoomButton
            sub={<p style={{ fontSize: 12, color: 'var(--text-2)', lineHeight: 1.6, margin: 0 }}><strong>Obiettivi</strong> orientati a promuovere la partecipazione sociale e contrastare le disuguaglianze e l'emarginazione.</p>}
            zoomSub={
              <PdzDialogList
                items={pdzSchedeDettaglioItems.filter(item => item.scheda.obiettivo_cluster === 'Inclusione')}
                selectedAmbitoId={selectedAmbitoId ?? null}
                emptyLabel="Nessuna scheda in Inclusione"
                detailContent={(item) => (
                  <SchedaDetailFull
                    scheda={item.scheda}
                    coloreGruppo={item.gruppoColore}
                    gruppoTitolo={item.gruppoTitolo}
                    ambitoLabel={selectedAmbitoId ? `Ambito di ${AMBITI_STATS.find(a => a.id === selectedAmbitoId)?.nome ?? ''}` : 'Piani di Zona MB'}
                  />
                )}
              />
            }
            footer={(openZoom) => (
              <button
                type="button"
                onClick={openZoom}
                style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%', padding: '6px 0', fontSize: 12, fontWeight: 600, color: '#6366f1', background: 'none', border: 'none', borderTop: '1px solid var(--border)', cursor: 'pointer', justifyContent: 'center', marginTop: 8, paddingTop: 8 }}
              >
                <List size={14} />
                Vedi elenco completo
              </button>
            )}
          />
          
          <BSDCard
            kind="kpi"
            sezione="Piano di Zona"
            label="Sistema"
            value={String(pdzSchedeDettaglioItems.filter(item => item.scheda.obiettivo_cluster === 'Sistema').length)}
            icon={Shield}
            color="#6366f1"
            zoomable
            hideZoomButton
            sub={<p style={{ fontSize: 12, color: 'var(--text-2)', lineHeight: 1.6, margin: 0 }}><strong>Obiettivi</strong> che rafforzano la governance, l'organizzazione e l'integrazione della rete dei servizi territoriali.</p>}
            zoomSub={
              <PdzDialogList
                items={pdzSchedeDettaglioItems.filter(item => item.scheda.obiettivo_cluster === 'Sistema')}
                selectedAmbitoId={selectedAmbitoId ?? null}
                emptyLabel="Nessuna scheda in Sistema"
                detailContent={(item) => (
                  <SchedaDetailFull
                    scheda={item.scheda}
                    coloreGruppo={item.gruppoColore}
                    gruppoTitolo={item.gruppoTitolo}
                    ambitoLabel={selectedAmbitoId ? `Ambito di ${AMBITI_STATS.find(a => a.id === selectedAmbitoId)?.nome ?? ''}` : 'Piani di Zona MB'}
                  />
                )}
              />
            }
            footer={(openZoom) => (
              <button
                type="button"
                onClick={openZoom}
                style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%', padding: '6px 0', fontSize: 12, fontWeight: 600, color: '#6366f1', background: 'none', border: 'none', borderTop: '1px solid var(--border)', cursor: 'pointer', justifyContent: 'center', marginTop: 8, paddingTop: 8 }}
              >
                <List size={14} />
                Vedi elenco completo
              </button>
            )}
          />
          <BSDCard
            kind="kpi"
            sezione="Piano di Zona"
            label="Interambito"
            value={String(pdzSchedeDettaglioItems.filter(item => item.scheda.obiettivo_cluster === 'Interambito').length)}
            icon={Layers}
            color="#6366f1"
            zoomable
            hideZoomButton
            sub={<p style={{ fontSize: 12, color: 'var(--text-2)', lineHeight: 1.6, margin: 0 }}><strong>Obiettivi</strong> condivisi tra più Ambiti territoriali per affrontare sfide comuni in modo coordinato.</p>}
            zoomSub={
              <PdzDialogList
                items={pdzSchedeDettaglioItems.filter(item => item.scheda.obiettivo_cluster === 'Interambito')}
                selectedAmbitoId={selectedAmbitoId ?? null}
                emptyLabel="Nessuna scheda in Interambito"
                detailContent={(item) => (
                  <SchedaDetailFull
                    scheda={item.scheda}
                    coloreGruppo={item.gruppoColore}
                    gruppoTitolo={item.gruppoTitolo}
                    ambitoLabel={selectedAmbitoId ? `Ambito di ${AMBITI_STATS.find(a => a.id === selectedAmbitoId)?.nome ?? ''}` : 'Piani di Zona MB'}
                  />
                )}
              />
            }
            footer={(openZoom) => (
              <button
                type="button"
                onClick={openZoom}
                style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%', padding: '6px 0', fontSize: 12, fontWeight: 600, color: '#6366f1', background: 'none', border: 'none', borderTop: '1px solid var(--border)', cursor: 'pointer', justifyContent: 'center', marginTop: 8, paddingTop: 8 }}
              >
                <List size={14} />
                Vedi elenco completo
              </button>
            )}
          />

        <BSDCard
          kind="panel"
          zoomable
          sezione="Piano di Zona"
          style={{ display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}
        >
          <h3 className="telemetry-section-title" style={{ marginTop: 0, marginBottom: 4 }}>Beneficiari</h3>
          <p className="telemetry-section-subtitle" style={{ margin: '0 0 6px', lineHeight: 1.3 }}>
            Ogni obiettivo può avere uno o più beneficiari
          </p>
          <div style={{ flex: 1, minHeight: 0, overflowY: 'hidden', overflowX: 'hidden' }}>
            <ResponsiveContainer width="100%" height={78}>
              <BarChart data={pdzData.schedePerCluster} layout="vertical" margin={{ top: 2, right: 22, bottom: 2, left: 6 }} barCategoryGap="18%">
                <XAxis type="number" hide allowDecimals={false} />
                <YAxis
                  type="category"
                  dataKey="name"
                  tick={<LeftAlignedAxisTick />}
                  axisLine={false}
                  tickLine={false}
                  width={BENEFICIARI_AXIS_LABEL_WIDTH}
                />
                <Tooltip content={<SchedeBarTooltip />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
                <Bar dataKey="count" radius={[0, 6, 6, 0]} barSize={13}>
                  <LabelList
                    dataKey="count"
                    position="right"
                    formatter={value => formatInt(Number(value ?? 0))}
                    fill="var(--text-2)"
                    fontSize={11}
                  />
                  {pdzData.schedePerCluster.map((entry, idx) => (
                    <Cell key={`${entry.id}-${idx}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </BSDCard>

       
        <BSDCard
          kind="kpi"
          sezione="Piano di Zona"
          label="Focus Disabilità"
          value={String(pdzSchedeDettaglioItems.filter(item => item.scheda.focus_disabilita).length)}
          icon={Accessibility}
          color="#6366f1"
          zoomable
          hideZoomButton
          sub={<p style={{ fontSize: 12, color: 'var(--text-2)', lineHeight: 1.6, margin: 0 }}><strong>Obiettivi</strong> che pongono un'attenzione specifica ai bisogni delle persone con disabilità.</p>}
          zoomSub={
            <PdzDialogList
              items={pdzSchedeDettaglioItems.filter(item => item.scheda.focus_disabilita)}
              selectedAmbitoId={selectedAmbitoId ?? null}
              emptyLabel="Nessun obiettivo con focus disabilità"
              detailContent={(item) => (
                <SchedaDetailFull
                  scheda={item.scheda}
                  coloreGruppo={item.gruppoColore}
                  gruppoTitolo={item.gruppoTitolo}
                  ambitoLabel={selectedAmbitoId ? `Ambito di ${AMBITI_STATS.find(a => a.id === selectedAmbitoId)?.nome ?? ''}` : 'Piani di Zona MB'}
                />
              )}
            />
          }
          footer={(openZoom) => (
            <button
              type="button"
              onClick={openZoom}
              style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%', padding: '6px 0', fontSize: 12, fontWeight: 600, color: '#6366f1', background: 'none', border: 'none', borderTop: '1px solid var(--border)', cursor: 'pointer', justifyContent: 'center', marginTop: 8, paddingTop: 8 }}
            >
              <List size={14} />
              Vedi elenco completo
            </button>
          )}
        />

        {/* Card 9 — Coinvolgimento ASST */}
        <BSDCard
          kind="kpi"
          sezione="Piano di Zona"
          label="Obiettivi con ASST"
          value={`${pdzData.coinvolgimentoAsstCount}`}
          icon={Stethoscope}
          color="#0ea5e9"
          zoomable
          hideZoomButton
          sub={<p style={{ fontSize: 12, color: 'var(--text-2)', lineHeight: 1.6, margin: 0 }}><strong>Obiettivi</strong> che prevedono il coinvolgimento diretto dell'ASST (Azienda Socio Sanitaria Territoriale) nella realizzazione delle azioni.</p>}
          zoomSub={
            <PdzDialogList
              items={pdzSchedeDettaglioItems.filter(item => item.scheda.coinvolgimentoASST)}
              selectedAmbitoId={selectedAmbitoId ?? null}
              emptyLabel="Nessun obiettivo con ASST coinvolta"
              detailContent={(item) => (
                <SchedaDetailFull
                  scheda={item.scheda}
                  coloreGruppo={item.gruppoColore}
                  gruppoTitolo={item.gruppoTitolo}
                  ambitoLabel={selectedAmbitoId ? `Ambito di ${AMBITI_STATS.find(a => a.id === selectedAmbitoId)?.nome ?? ''}` : 'Piani di Zona MB'}
                />
              )}
            />
          }
          footer={(openZoom) => (
            <button
              type="button"
              onClick={openZoom}
              style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%', padding: '6px 0', fontSize: 12, fontWeight: 600, color: '#6366f1', background: 'none', border: 'none', borderTop: '1px solid var(--border)', cursor: 'pointer', justifyContent: 'center', marginTop: 8, paddingTop: 8 }}
            >
              <List size={14} />
              Vedi elenco completo
            </button>
          )}
        />

        

        {pdzData.hasSelectedPiano && <>
          
          
      {/*
          <BSDCard
            kind="panel"
            zoomable
            sezione="Piano di Zona"
            style={{ display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}
          >
            <h3 className="telemetry-section-title" style={{ marginTop: 0, marginBottom: 8 }}>Categorie Azioni</h3>
            <div style={{ flex: 1, minHeight: 0, maxHeight: 180, overflowY: 'auto', overflowX: 'hidden', paddingRight: 4 }}>
              <ResponsiveContainer width="100%" height={azioniPerGruppoChartHeight}>
                <BarChart data={pdzData.azioniPerGruppo} layout="vertical" margin={{ top: 4, right: 22, bottom: 4, left: 6 }}>
                  <XAxis type="number" hide allowDecimals={false} />
                  <YAxis type="category" dataKey="name" tick={<LeftAlignedAxisTick />} axisLine={false} tickLine={false} width={AZIONI_AXIS_LABEL_WIDTH} />
                  <Tooltip content={<AzioniPieTooltip />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
                  <Bar dataKey="value" radius={[0, 6, 6, 0]} barSize={14}>
                    <LabelList
                      dataKey="value"
                      position="right"
                      formatter={value => formatInt(Number(value ?? 0))}
                      fill="var(--text-2)"
                      fontSize={11}
                    />
                    {pdzData.azioniPerGruppo.map((entry, idx) => (
                      <Cell key={`azioni-${entry.id}-${idx}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </BSDCard>

          */}
          
        </>}
      </AutoGrid>

      {!pdzData.hasSelectedPiano && (
        <div
          style={{
            border: '1px solid var(--border-md)',
            borderRadius: 10,
            padding: '12px 14px',
            background: 'var(--bg-2)',
            color: 'var(--text-2)',
            fontSize: 12,
          }}
        >
          Nessuna scheda PdZ disponibile.
        </div>
      )}

      {selectedAmbitoId === null && (
        <>
          <h3 className="telemetry-section-title" style={{ marginTop: 16, marginBottom: 8 }}>
            Comparazione tra ambiti
          </h3>

          <AutoGrid>
            <CompareAmbitiBarCard
              sezione="Piano di Zona"
              title="Obiettivi"
              data={pdzCompareData.map(d => ({ id: d.id, name: d.name, value: d.totaleSchede, color: d.color }))}
              formatter={v => String(v)}
              valueLabel="obiettivi"
            />

            <CompareAmbitiBarCard
              sezione="Piano di Zona"
              title="Benessere sociale"
              data={pdzCompareData.map(d => ({ id: d.id, name: d.name, value: d.benessere, color: d.color }))}
              formatter={v => String(v)}
              valueLabel="obiettivi"
            />

             <CompareAmbitiBarCard
              sezione="Piano di Zona"
              title="Inclusione"
              data={pdzCompareData.map(d => ({ id: d.id, name: d.name, value: d.inclusione, color: d.color }))}
              formatter={v => String(v)}
              valueLabel="obiettivi"
            />

             <CompareAmbitiBarCard
              sezione="Piano di Zona"
              title="Sistema"
              data={pdzCompareData.map(d => ({ id: d.id, name: d.name, value: d.sistema, color: d.color }))}
              formatter={v => String(v)}
              valueLabel="obiettivi"
            />

             <CompareAmbitiBarCard
              sezione="Piano di Zona"
              title="Focus Disabilità"
              data={pdzCompareData.map(d => ({ id: d.id, name: d.name, value: d.focusDisabilita, color: d.color }))}
              formatter={v => String(v)}
              valueLabel="obiettivi"
            />
           
            

             <CompareAmbitiBarCard
              sezione="Piano di Zona"
              title="Coinvolgimento ASST"
              data={pdzCompareData.map(d => ({ id: d.id, name: d.name, value: d.asst, color: d.color }))}
              formatter={v => String(v)}
              valueLabel="obiettivi"
            />


            <CompareAmbitiBarCard
              sezione="Piano di Zona"
              title="Beneficiari Adulti"
              data={pdzCompareData.map(d => ({ id: d.id, name: d.name, value: d.adulti, color: d.color }))}
              formatter={v => String(v)}
              valueLabel="obiettivi"
            />
            <CompareAmbitiBarCard
              sezione="Piano di Zona"
              title="Beneficiari Minori"
              data={pdzCompareData.map(d => ({ id: d.id, name: d.name, value: d.minori, color: d.color }))}
              formatter={v => String(v)}
              valueLabel="obiettivi"
            />

             <CompareAmbitiBarCard
              sezione="Piano di Zona"
              title="Beneficiari Anziani"
              data={pdzCompareData.map(d => ({ id: d.id, name: d.name, value: d.anziani, color: d.color }))}
              formatter={v => String(v)}
              valueLabel="obiettivi"
            />

            
          </AutoGrid>

         
        </>
      )}
    </>
  )
}
